"use client";

import { create } from "zustand";
import type { AuthUser } from "@/lib/api-client";

// Multi-level, one-page navigation. There is no menu bar: the Home screen is
// the menu, the user drills into an area, then into a detail, and the header
// back arrow pops one level.
//
//   Level 0  home
//   Level 1  an area      (browse, my-reservations, host, moderator, ...)
//   Level 2  a detail     (product)
//
// Opening an area always resets to [home, area] (areas are destinations, not
// a stack); opening a product appends one detail level on top of the current
// area, so "back" always returns to where the user came from.

export type ViewName =
  | "home"
  | "browse"
  | "product"
  | "my-reservations"
  | "host"
  | "admin"
  | "moderator"
  | "expenses"
  | "dashboard"
  | "notifications";

const AREA_VIEWS: ViewName[] = [
  "browse",
  "my-reservations",
  "host",
  "admin",
  "moderator",
  "expenses",
  "dashboard",
  "notifications",
];

type AppState = {
  // navigation
  path: ViewName[];
  selectedProductId: string | null;
  openArea: (v: ViewName) => void;
  openProduct: (id: string) => void;
  back: () => void;
  goHome: () => void;

  // auth
  user: AuthUser | null;
  authLoading: boolean;
  setUser: (u: AuthUser | null) => void;
  setAuthLoading: (b: boolean) => void;
  // admin role-preview (impersonation)
  impersonating: boolean;
  setImpersonating: (b: boolean) => void;
  // effective feature permissions (from /api/auth/me)
  permissions: string[];
  setPermissions: (p: string[]) => void;

  // auth panel
  authOpen: boolean;
  authMode: "login" | "register";
  openAuth: (mode: "login" | "register") => void;
  closeAuth: () => void;

  // product editor
  productEditorOpen: boolean;
  editingProductId: string | null;
  openNewProduct: () => void;
  openEditProduct: (id: string) => void;
  closeProductEditor: () => void;

  // admin user editor
  userEditorOpen: boolean;
  editingUserId: string | null;
  openNewUser: () => void;
  openEditUser: (id: string) => void;
  closeUserEditor: () => void;
};

export const useAppStore = create<AppState>((set) => ({
  path: ["home"],
  selectedProductId: null,
  openArea: (v) =>
    set((s) => {
      // Already there: nothing to do.
      if (s.path[s.path.length - 1] === v && s.path.length === 2) return s;
      return { path: ["home", v], selectedProductId: null };
    }),
  openProduct: (id) =>
    set((s) => {
      const p = [...s.path];
      if (p[p.length - 1] === "product") {
        // Replacing the current detail (e.g. house -> house from a list).
        return { path: p, selectedProductId: id };
      }
      return { path: [...p, "product"], selectedProductId: id };
    }),
  back: () =>
    set((s) => ({
      path: s.path.length > 1 ? s.path.slice(0, -1) : s.path,
      selectedProductId: null,
    })),
  goHome: () => set({ path: ["home"], selectedProductId: null }),

  user: null,
  authLoading: true,
  setUser: (u) => set({ user: u }),
  setAuthLoading: (b) => set({ authLoading: b }),

  impersonating: false,
  setImpersonating: (b) => set({ impersonating: b }),

  permissions: [],
  setPermissions: (p) => set({ permissions: p }),

  authOpen: false,
  authMode: "login",
  openAuth: (mode) => set({ authOpen: true, authMode: mode }),
  closeAuth: () => set({ authOpen: false }),

  productEditorOpen: false,
  editingProductId: null,
  openNewProduct: () => set({ productEditorOpen: true, editingProductId: null }),
  openEditProduct: (id) => set({ productEditorOpen: true, editingProductId: id }),
  closeProductEditor: () => set({ productEditorOpen: false, editingProductId: null }),

  userEditorOpen: false,
  editingUserId: null,
  openNewUser: () => set({ userEditorOpen: true, editingUserId: null }),
  openEditUser: (id) => set({ userEditorOpen: true, editingUserId: id }),
  closeUserEditor: () => set({ userEditorOpen: false, editingUserId: null }),
}));

/** The view currently on screen. */
export function currentView(path: ViewName[]): ViewName {
  return path[path.length - 1] ?? "home";
}

/** True when the view is a top-level destination (never a detail level). */
export function isArea(v: ViewName): boolean {
  return AREA_VIEWS.includes(v);
}
